import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useFontAccessibility } from '../context/FontAccessibilityContext';
import { Menu, ChevronDown, X, Type, Minus, Plus } from 'lucide-react';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [departmentsDropdown, setDepartmentsDropdown] = useState(false);
  const [citizenCornerDropdown, setCitizenCornerDropdown] = useState(false);
  const [galleryDropdown, setGalleryDropdown] = useState(false);
  const [informationDropdown, setInformationDropdown] = useState(false);
  const [loginDropdown, setLoginDropdown] = useState(false);
  const [fontSizeDropdown, setFontSizeDropdown] = useState(false);
  const location = useLocation();
  const { language, toggleLanguage } = useLanguage();
  const { fontSize, increaseFontSize, decreaseFontSize, resetFontSize } = useFontAccessibility();
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [consumerCoopMobile, setConsumerCoopMobile] = useState(false);
  const [bankingMobile, setBankingMobile] = useState(false);
  const [creditMobile, setCreditMobile] = useState(false);
  const [servicesBankMobile, setServicesBankMobile] = useState(false);

  const [marundhagamMobile, setMarundhagamMobile] = useState(false);


  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1024);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);



  // Refs for dropdown menus
  const departmentsRef = useRef(null);
  const informationRef = useRef(null);
  const galleryRef = useRef(null);
  const citizenCornerRef = useRef(null);
  const loginRef = useRef(null);
  const fontSizeRef = useRef(null);
  const institutionsRef = useRef(null);
  const policyRef = useRef(null);
  const servicesRef = useRef(null);
  const schemesRef = useRef(null);
  const content = {
    en: {
      home: 'Home',
      about: 'About Us',
      institutions: 'Institutions',
      policy: 'Policy',
      services: 'Services',
      schemes: 'Schemes',
      login: 'Login',
      contact: 'Contact Us',
      title: 'COOPERATIVE SOCIETIES TAMIL NADU',
      screenReader: 'Screen Reader Access',
      fontSize: 'Font Size',
      increase: 'Increase Font',
      decrease: 'Decrease Font',
      reset: 'Reset Font',

      // Institutions sub-items
      consumerCooperatives: 'Consumer Cooperatives',
      banking: 'Banking',
      creditSocieties: 'Credit Societies',

      // Policy sub-items
      actsRules: 'Acts & Rules',
      gos: 'GOs',
      circulars: 'Circulars',

      // Services sub-items
      evadagai: 'eVadagai',
      bankingService: 'Banking',

      // Schemes sub-items
      kcc: 'KCC',
      loanMechanisation: 'Loan Mechanisation',
      autoProvision: 'Auto Provision',
      womenLand: 'Women Land',

      // Login sub-items
      ercs: 'eRCS',
      dms: 'DMS',
      dashboard: 'Dashboard',
      list: 'LIST'
    },
    ta: {
      home: 'முகப்பு',
      about: 'எங்களைப் பற்றி',
      institutions: 'நிறுவனங்கள்',
      policy: 'கொள்கை',
      services: 'சேவைகள்',
      schemes: 'திட்டங்கள்',
      login: 'உள்நுழைவு',
      contact: 'தொடர்பு கொள்ளவும்',
      title: 'கூட்டுறவுச் சங்கங்கள் தமிழ்நாடு',
      screenReader: 'திரை வாசிப்பு அணுகல்',
      fontSize: 'எழுத்துரு அளவு',
      increase: 'எழுத்துரு அதிகரிக்க',
      decrease: 'எழுத்துரு குறைக்க',
      reset: 'மீட்டமைக்க',

      // Institutions sub-items
      consumerCooperatives: 'நுகர்வோர் கூட்டுறவு',
      banking: 'வங்கி',
      creditSocieties: 'கடன் சங்கங்கள்',

      // Policy sub-items
      actsRules: 'சட்டங்கள் & விதிகள்',
      gos: 'அரசாணைகள்',
      circulars: 'சுற்றறிக்கைகள்',

      // Services sub-items
      evadagai: 'மின்-வடகை',
      bankingService: 'வங்கி',

      // Schemes sub-items
      kcc: 'கே.சி.சி',
      loanMechanisation: 'கடன் இயந்திரமயமாக்கல்',
      autoProvision: 'தானியங்கி ஏற்பாடு',
      womenLand: 'பெண்கள் நிலம்',

      // Login sub-items
      ercs: 'eRCS',
      dms: 'DMS',
      dashboard: 'டாஷ்போர்டு',
      list: 'பட்டியல்'
    }
  };

  const t = content[language];

  const handleLanguageToggle = () => {
    const newLanguage = language === 'en' ? 'ta' : 'en';
    localStorage.setItem('preferredLanguage', newLanguage);
    toggleLanguage();
  };

  useEffect(() => {
    const savedLanguage = localStorage.getItem('preferredLanguage');
    if (savedLanguage && savedLanguage !== language) {
      toggleLanguage();
    }
  }, []);

  const closeAllDropdowns = () => {
    setDepartmentsDropdown(false);
    setCitizenCornerDropdown(false);
    setGalleryDropdown(false);
    setInformationDropdown(false);
    setLoginDropdown(false);
    setFontSizeDropdown(false);
  };

  const handleMobileMenuToggle = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsOpen(!isOpen);
    if (isOpen) {
      closeAllDropdowns();
    }
  };

  const handleMenuItemClick = () => {
    setIsOpen(false);
    closeAllDropdowns();
  };

  const handleDropdownClick = (dropdownType, e) => {
    e.preventDefault();
    e.stopPropagation();

    switch (dropdownType) {
      case 'departments':
        setDepartmentsDropdown(!departmentsDropdown);
        setCitizenCornerDropdown(false);
        setGalleryDropdown(false);
        setInformationDropdown(false);
        setFontSizeDropdown(false);
        break;
      case 'citizenCorner':
        setCitizenCornerDropdown(!citizenCornerDropdown);
        setDepartmentsDropdown(false);
        setGalleryDropdown(false);
        setInformationDropdown(false);
        setFontSizeDropdown(false);
        break;
      case 'gallery':
        setGalleryDropdown(!galleryDropdown);
        setDepartmentsDropdown(false);
        setCitizenCornerDropdown(false);
        setInformationDropdown(false);
        setFontSizeDropdown(false);
        break;
      case 'information':
        setInformationDropdown(!informationDropdown);
        setDepartmentsDropdown(false);
        setCitizenCornerDropdown(false);
        setGalleryDropdown(false);
        setFontSizeDropdown(false);
        break;
      case 'fontSize':
        setFontSizeDropdown(!fontSizeDropdown);
        setDepartmentsDropdown(false);
        setCitizenCornerDropdown(false);
        setGalleryDropdown(false);
        setInformationDropdown(false);
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      const mobileMenu = document.querySelector('.mobile-menu-container');
      const mobileToggle = document.querySelector('.mobile-toggle');

      if (isOpen && mobileMenu && !mobileMenu.contains(event.target) && !mobileToggle.contains(event.target)) {
        setIsOpen(false);
        closeAllDropdowns();
      }
    };

    if (isOpen) {
      document.addEventListener('click', handleClickOutside);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  useEffect(() => {
    setIsOpen(false);
    closeAllDropdowns();
  }, [location.pathname]);
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        institutionsRef.current && !institutionsRef.current.contains(event.target) &&
        policyRef.current && !policyRef.current.contains(event.target) &&
        servicesRef.current && !servicesRef.current.contains(event.target) &&
        schemesRef.current && !schemesRef.current.contains(event.target)
      ) {
        setActiveDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 100) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <header className="w-full top-0 left-0 z-50">
      {/* Skip to Content Links for Screen Readers */}
      <div className="sr-only">
        <a href="#main-content" className="skip-link">Skip to main content</a>
        <a href="#navigation" className="skip-link">Skip to navigation</a>
      </div>

      {/* TOP HEADER WITH LOGOS, TITLE & ACCESSIBILITY */}
{/* TOP HEADER WITH LOGOS, TITLE & ACCESSIBILITY */}
<div className="bg-white border-b border-gray-200 w-full relative z-[60]">
  <div className="w-full px-2 py-1.5 sm:px-4 sm:py-3 flex items-center justify-between">

    {/* LEFT LOGO */}
    <div className="flex-shrink-0">
      <img
        src="/Images/cooplogo.png"
        alt="Cooperative Logo"
        className="h-8 w-8 sm:h-12 sm:w-12 lg:h-16 lg:w-16 object-contain"
      />
    </div>

    {/* CENTER TITLE - Conditional based on screen size */}
<div className="flex-1 flex flex-col items-center text-center px-2 lg:absolute lg:left-1/2 lg:transform lg:-translate-x-1/2">
  {/* Mobile & Tablet: Show based on language - REDUCED FONT SIZE */}
  <div className="lg:hidden">
    {language === 'en' ? (
      <h1 className="text-[#D32F2F] font-bold leading-tight text-xs sm:text-sm md:text-base">
        Tamil Nadu Cooperative Societies
      </h1>
    ) : (
      <h2 className="text-gray-700 font-semibold leading-tight text-[10px] sm:text-xs md:text-sm">
        தமிழ்நாட்டின் கூட்டுறவு சங்கங்கள்
      </h2>
    )}
  </div>

  {/* Desktop: Show both */}
  <div className="hidden lg:flex lg:flex-col lg:items-center">
    <h1 className="text-[#D32F2F] font-bold leading-tight text-[22px]">
      Tamil Nadu Cooperative Societies
    </h1>
    <h2 className="text-gray-700 font-semibold mt-1 leading-tight text-[18px]">
      தமிழ்நாட்டின் கூட்டுறவு சங்கங்கள்
    </h2>
  </div>
</div>

          {/* RIGHT SECTION - Mobile Menu + Logo */}
          <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">

            {/* Accessibility Buttons - Desktop Only */}
            <div className="hidden lg:flex items-center gap-2">
              {/* A- Button */}
              <button
                onClick={decreaseFontSize}
                className="h-9 w-9 flex items-center justify-center border-2 border-[#1976D2] 
            text-[#1976D2] bg-white rounded text-sm font-bold hover:bg-[#E3F2FD] 
            transition-colors cursor-pointer"
                aria-label="Decrease font size"
              >
                A−
              </button>

              {/* A+ Button */}
              <button
                onClick={increaseFontSize}
                className="h-9 w-9 flex items-center justify-center border-2 border-[#1976D2] 
            text-[#1976D2] bg-white rounded text-sm font-bold hover:bg-[#E3F2FD] 
            transition-colors cursor-pointer"
                aria-label="Increase font size"
              >
                A+
              </button>

              {/* Reset Button */}
              <button
                onClick={resetFontSize}
                className="h-9 w-9 flex items-center justify-center border-2 border-[#1976D2] 
            text-[#1976D2] bg-white rounded hover:bg-[#E3F2FD] 
            transition-colors cursor-pointer"
                aria-label="Reset font size"
              >
                <Type size={16} />
              </button>

              {/* Screen Reader Button */}
              <button
                className="h-9 w-9 flex items-center justify-center border-2 border-[#1976D2] 
            text-[#1976D2] bg-white rounded hover:bg-[#E3F2FD] 
            transition-colors cursor-pointer"
                aria-label="Screen reader access"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M12 6v12"></path>
                  <path d="M17.196 9 6.804 15"></path>
                  <path d="m6.804 9 10.392 6"></path>
                  <circle cx="12" cy="12" r="10"></circle>
                </svg>
              </button>

              {/* Language Toggle - En/த */}
              <div className="flex items-center gap-0 border-2 border-[#1976D2] rounded overflow-hidden">
                <button
                  onClick={handleLanguageToggle}
                  className={`h-9 px-3 flex items-center justify-center text-sm font-semibold 
              transition-colors cursor-pointer border-r-2 border-[#1976D2]
              ${language === 'en' ? 'bg-[#1976D2] text-white' : 'bg-white text-[#1976D2] hover:bg-[#E3F2FD]'}`}
                  aria-label="Switch to English"
                >
                  En
                </button>

                <button
                  onClick={handleLanguageToggle}
                  className={`h-9 px-3 flex items-center justify-center text-sm font-semibold 
              transition-colors cursor-pointer
              ${language === 'ta' ? 'bg-[#1976D2] text-white' : 'bg-white text-[#1976D2] hover:bg-[#E3F2FD]'}`}
                  aria-label="Switch to Tamil"
                >
                  த
                </button>
              </div>
            </div>

       {/* Mobile Menu Toggle */}
<button
  className="lg:hidden mobile-toggle p-1.5 rounded-md text-[#1976D2] border-2 border-[#1976D2]"
  onClick={handleMobileMenuToggle}
>
  {isOpen ? <X size={20} /> : <Menu size={20} />}
</button>
          {/* RIGHT LOGO */}
<div className="flex-shrink-0">
  <img
    src="/Images/tn-logo.png"
    alt="Tamil Nadu Logo"
    className="h-8 w-8 sm:h-12 sm:w-12 lg:h-16 lg:w-16 object-contain"
  />
</div>
          </div>

        </div>
      </div>


      {/* NAVIGATION BAR - Blue - BOTTOM LAYER - FIXED - HIDDEN ON MOBILE */}
      <div id="navigation" className={`hidden lg:block bg-gradient-to-r from-[#005A8D] via-[#006AA5] to-[#005A8D] text-white w-full shadow-md transition-all duration-300 ${isScrolled ? 'fixed top-0 left-0 z-50' : ''} -mb-5`}>
        <div className="w-full px-4 sm:px-4 lg:px-8 py-3">
          <div className="flex items-center justify-center max-w-7xl mx-auto">

            {/* Desktop Navigation Menu - All Centered */}
            <nav className="hidden lg:flex items-center justify-center w-full" role="navigation" aria-label="Main navigation">
              <ul className="flex items-center space-x-4 font-medium">
                {/* Home */}
                <li>
                  <Link
                    to="/"
                    className={`px-4 py-2 rounded-md transition-all duration-200 text-lg ${location.pathname === '/'
                      ? 'bg-white bg-opacity-20 text-white font-semibold'
                      : 'hover:bg-white hover:bg-opacity-10'
                      }`}
                    aria-current={location.pathname === '/' ? 'page' : undefined}
                  >
                    {t?.home || 'Home'}
                  </Link>
                </li>

                {/* About Us */}
                <li>
                  <Link
                    to="/about-us"
                    className="px-4 py-2 rounded-md hover:bg-white hover:bg-opacity-10 transition-all duration-200 text-lg"
                  >
                    {t?.about || 'About Us'}
                  </Link>
                </li>


                {/* Institution Dropdown */}
                <li
                  className="relative group"
                  ref={institutionsRef}
                  onClick={() =>
                    isMobile
                      ? setActiveDropdown(activeDropdown === "institutions" ? null : "institutions")
                      : null
                  }
                  onMouseEnter={!isMobile ? () => setActiveDropdown("institutions") : undefined}
                  onMouseLeave={!isMobile ? () => setActiveDropdown(null) : undefined}
                >
                  <button className="px-4 py-2 rounded-md hover:bg-white hover:bg-opacity-10 transition-all duration-200 flex items-center">
                    <span className="text-lg font-medium">{t?.institutions || 'Institutions'}</span>
                    <svg
                      className={`ml-1 w-4 h-4 transform transition-transform duration-200 ${activeDropdown === "institutions" ? "rotate-180" : ""
                        }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>
                  {activeDropdown === "institutions" && (
                    <ul className="absolute left-0 bg-white shadow-lg rounded-md mt-0 w-60 z-50 text-black">
                      <li className="relative group/coop">
                        <button className="w-full text-left px-4 py-2 hover:bg-gray-100 flex justify-between items-center">
                          Consumer Cooperatives
                          <ChevronDown size={16} className="ml-2 transition-transform group-hover/coop:rotate-180" />
                        </button>

                        {/* Submenu */}
                        <ul className="absolute left-full top-0 bg-white shadow-lg rounded-md w-56 text-black 
                 opacity-0 invisible group-hover/coop:opacity-100 group-hover/coop:visible 
                 transition-all duration-200">
                          <li>
                            <a href="/pds" className="block px-4 py-2 hover:bg-gray-100">PDS</a>
                          </li>
                          <li>
                            <a href="/dccws" className="block px-4 py-2 hover:bg-gray-100">DCCWS</a>
                          </li>
                          <li>
                            <a href="/dccws" className="block px-4 py-2 hover:bg-gray-100">Primary Stores</a>
                          </li>
                          <li className="relative group/marun">
                            <button
                              className="w-full text-left px-4 py-2 flex justify-between items-center hover:bg-gray-100"
                            >
                              Marundhagam
                              <ChevronDown size={14} className="transition-transform group-hover/marun:rotate-180" />
                            </button>

                            {/* Marundhagam Submenu */}
                            <ul
                              className="absolute left-full top-0 bg-white shadow-lg rounded-md w-60 text-black
               opacity-0 invisible group-hover/marun:opacity-100 group-hover/marun:visible
               transition-all duration-200"
                            >
                              <li>
                                <a href="/mudhalvar"
                                  className="block px-4 py-2 hover:bg-gray-100">
                                  Mudhalvar Marundhagam
                                </a>
                              </li>

                              <li>
                                <a href="/kooturavu-marundhagam"
                                  className="block px-4 py-2 hover:bg-gray-100">
                                  Kooturavu Marunthagam
                                </a>
                              </li>

                              <li>
                                <a href="/amma-marundhagam"
                                  className="block px-4 py-2 hover:bg-gray-100">
                                  Amma Marunthagam
                                </a>
                              </li>
                            </ul>
                          </li>

                        </ul>
                      </li>

                      <li className="relative group/bank">
                        <button className="w-full text-left px-4 py-2 hover:bg-gray-100 flex justify-between items-center ">
                          Banking
                          <ChevronDown size={16} className="ml-2 transition-transform group-hover/bank:rotate-180" />
                        </button>

                        {/* Banking Submenu */}
                        <ul className="absolute left-full top-0 bg-white shadow-lg rounded-md w-56 text-black 
                 opacity-0 invisible group-hover/bank:opacity-100 group-hover/bank:visible 
                 transition-all duration-200">
                          <li>
                            <a href="/tnsc" className="block px-4 py-2 hover:bg-gray-100">
                              TNSACB
                            </a>
                          </li>
                          <li>
                            <a href="/dccwss" className="block px-4 py-2 hover:bg-gray-100">
                              DCCB
                            </a>
                          </li>
                          <li>
                            <a href="/ucb" className="block px-4 py-2 hover:bg-gray-100">
                              UCB
                            </a>
                          </li>
                        </ul>
                      </li>

                      <li className="relative group/credit">
                        <button className="w-full text-left px-4 py-2 hover:bg-gray-100 flex justify-between items-center font-semibold">

                          Credit Societies
                          <ChevronDown size={16} className="ml-2 transition-transform group-hover/credit:rotate-180" />
                        </button>

                        {/* Credit Societies Submenu */}
                        <ul className="absolute left-full top-0 bg-white shadow-lg rounded-md w-56 text-black 
                 opacity-0 invisible group-hover/credit:opacity-100 group-hover/credit:visible 
                 transition-all duration-200">
                          <li>
                            <a href="/paccs" className="block px-4 py-2 hover:bg-gray-100">
                              PACCS
                            </a>
                          </li>
                          <li>
                            <a href="/paccs" className="block px-4 py-2 hover:bg-gray-100">
                              UCCS
                            </a>
                          </li>
                        </ul>
                      </li>
                         <li>
                        <a
                          href="agri-marketing"
                          className="block px-4 py-2 hover:bg-gray-100"
                        >
                          Agri Marketing
                        </a>
                      </li>
                    </ul>
                  )}
                </li>


                {/* Policy Dropdown */}
                <li
                  className="relative"
                  ref={policyRef}
                  onMouseEnter={() => setActiveDropdown("policy")}
                  onMouseLeave={() => setActiveDropdown(null)}
                >

                  <button className="px-4 py-2 rounded-md hover:bg-white hover:bg-opacity-10 transition-all duration-200 flex items-center">
                    <span className="text-lg font-medium">
                      {t?.policy || 'Policy'}</span>
                    <svg
                      className={`ml-1 w-4 h-4 transform transition-transform duration-200 ${activeDropdown === "policy" ? "rotate-180" : ""
                        }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>
                  {activeDropdown === "policy" && (
                    <ul className="absolute left-0 bg-white shadow-lg rounded-md mt-0 w-52 z-50 text-black ">
                      <li>
                        <a
                          href="/policy/acts-and-rules"
                          className="block px-4 py-2 hover:bg-gray-100"
                        >
                          Acts & Rules
                        </a>
                      </li>
                      <li>
                        <a
                          href="/policy/gos"
                          className="block px-4 py-2 hover:bg-gray-100"
                        >
                          GOs
                        </a>
                      </li>
                      <li>
                        <a
                          href="/policy/circulars"
                          className="block px-4 py-2 hover:bg-gray-100"
                        >
                          Circulars
                        </a>
                      </li>
                    </ul>
                  )}
                </li>


                {/* Servicez Dropdown */}
                <li
                  className="relative"
                  ref={servicesRef}
                  onMouseEnter={() => setActiveDropdown("services")}
                  onMouseLeave={() => setActiveDropdown(null)}
                >
                  <button className="px-4 py-2 rounded-md hover:bg-white hover:bg-opacity-10 transition-all duration-200 flex items-center">
                    <span className="text-lg font-medium">{t?.services || 'Services'}</span>
                    <svg
                      className={`ml-1 w-4 h-4 transform transition-transform duration-200 ${activeDropdown === "services" ? "rotate-180" : ""
                        }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>
                  {activeDropdown === "services" && (
                    <ul className="absolute left-0 bg-white shadow-lg rounded-md mt-0 w-52 z-50 text-black">
                      <li>
                        <a
                          href="/services/evadagai"
                          className="block px-4 py-2 hover:bg-gray-100"
                        >
                          eVadagai
                        </a>
                      </li>
                      <li className="relative group/serviceBank">
                        <button className="w-full text-left px-4 py-2 hover:bg-gray-100 flex justify-between items-center font-semibold">
                          Banking
                          <ChevronDown size={16} className="ml-2 transition-transform group-hover/serviceBank:rotate-180" />
                        </button>

                        {/* Banking submenu */}
                        <ul className="absolute left-full top-0 bg-white shadow-lg rounded-md w-56 text-black 
                 opacity-0 invisible group-hover/serviceBank:opacity-100 group-hover/serviceBank:visible 
                 transition-all duration-200">
                          <li>
                            <a href="https://www.rcs.tn.gov.in/loan/" target='blank' className="block px-4 py-2 hover:bg-gray-100">
                              Bank Loan
                            </a>
                          </li>
                          <li>
                            <a href="/services/banking/account-opening" className="block px-4 py-2 hover:bg-gray-100">
                              Account Opening
                            </a>
                          </li>
                        </ul>
                      </li>
                       <a
                          href="/quick-commerce"
                          className="block px-4 py-2 hover:bg-gray-100"
                        >
                          Quick Commerce
                        </a>
                         <a
                          href="/society-registration"
                          className="block px-4 py-2 hover:bg-gray-100"
                        >
                          Society Registarion 
                        </a>
                         <a
                          href="/agri-mechanism"
                          className="block px-4 py-2 hover:bg-gray-100"
                        >
                          Agri Mechanism
                        </a>

                    </ul>
                  )}
                </li>

                {/* Schemes Dropdown */}
                <li
                  className="relative"
                  ref={schemesRef}
                  onMouseEnter={() => setActiveDropdown("schemes")}
                  onMouseLeave={() => setActiveDropdown(null)}
                >
                  <button className="px-4 py-2 rounded-md hover:bg-white hover:bg-opacity-10 transition-all duration-200 flex items-center">
                    <span className="text-lg font-medium">
                      Schemes</span>
                    <svg
                      className={`ml-1 w-4 h-4 transform transition-transform duration-200 ${activeDropdown === "schemes" ? "rotate-180" : ""
                        }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>
                  {activeDropdown === "schemes" && (
                    <ul className="absolute left-0 bg-white shadow-lg rounded-md mt-0 w-60 z-50 text-black">
                      <li>
                        <a
                          href="/kcc"
                          className="block px-4 py-2 hover:bg-gray-100"
                        >
                          KCC
                        </a>
                      </li>
                      <li>
                        <a
                          href="/loan"
                          className="block px-4 py-2 hover:bg-gray-100"
                        >
                          Loan Mechanisation
                        </a>
                      </li>
                      <li>
                        <a
                          href="/auto"
                          className="block px-4 py-2 hover:bg-gray-100"
                        >
                          Auto Provision
                        </a>
                      </li>
                      <li>
                        <a
                          href="/schemes/women-land"
                          className="block px-4 py-2 hover:bg-gray-100"
                        >
                          Women Land
                        </a>
                      </li>
                      <li>
                        <a
                          href="/thayumanavar"
                          className="block px-4 py-2 hover:bg-gray-100"
                        >
                      Thaayumaanavar 
                        </a>
                      </li>
                    </ul>
                  )}
                </li>


                {/* Login Dropdown */}
                <li className="relative" ref={loginRef}>
                  <button
                    className="flex items-center gap-1 px-4 py-2 rounded-md transition-all duration-200 font-medium"
                    onMouseEnter={() => setLoginDropdown(true)}
                    onMouseLeave={() => setLoginDropdown(false)}
                    aria-expanded={loginDropdown}
                    aria-haspopup="true"
                  >
                    <span className="text-lg font-medium">{t?.login || 'Login'}</span>
                    <ChevronDown size={16} className={`transition-transform duration-200 ${loginDropdown ? 'rotate-180' : ''}`} />
                  </button>

                  <div
                    className={`absolute top-full right-0 mt-1 text-gray-800 shadow-xl rounded-md w-40 transition-all duration-200 ${loginDropdown ? 'opacity-100 visible' : 'opacity-0 invisible'
                      }`}
                    onMouseEnter={() => setLoginDropdown(true)}
                    onMouseLeave={() => setLoginDropdown(false)}
                    role="menu"
                  >
                    <div className="py-2 bg-white">
                      <a href="http://ercs.kooturavu.tn.gov.in/" target="_blank" className="block px-4 py-2.5 hover:bg-blue-50  transition-colors" role="menuitem">
                        eRCS
                      </a>
                      <a href="https://www.santhai.kooturavu.tn.gov.in/login" target="_blank" rel="noopener noreferrer" className="block px-4 py-2.5 hover:bg-blue-50  transition-colors" role="menuitem">
                        DMS
                      </a>
                      <a href="https://dashboard.kooturavu.tn.gov.in/" target="_blank" className="block px-4 py-2.5 hover:bg-blue-50  transition-colors" role="menuitem">
                        Dashboard
                      </a>
                      <a href="#" className="block px-4 py-2.5 hover:bg-blue-50 transition-colors" role="menuitem">
                        LIST
                      </a>
                    </div>
                  </div>
                </li>
              </ul>
            </nav>
          </div>
        </div>
      </div>

      {/* Mobile Navigation Menu */}
      {isOpen && (
        <>
          <div
            className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-40"
            onClick={() => setIsOpen(false)}
            aria-hidden="true"
          ></div>

          <div className="mobile-menu-container lg:hidden fixed top-0 left-0 bg-white shadow-2xl z-50 w-80 h-screen overflow-y-auto" role="dialog" aria-modal="true">
            {/* Mobile Menu Header */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-4 flex justify-between items-center sticky top-0 z-50">
              <h2 className="font-bold text-lg">Menu</h2>
              <div className="flex items-center gap-3">
                {/* Language Toggle */}
                <label className="flex items-center cursor-pointer">
                  <div className="relative">
                    <input
                      type="checkbox"
                      className="sr-only"
                      onChange={handleLanguageToggle}
                      checked={language === 'ta'}
                      aria-label={`Switch to ${language === 'en' ? 'Tamil' : 'English'}`}
                    />
                    <div className="block w-12 h-6 bg-white bg-opacity-30 rounded-full"></div>
                    <div
                      className="absolute left-0.5 top-0.5 bg-white w-5 h-5 rounded-full transition-transform duration-200 flex items-center justify-center text-xs font-bold text-blue-700"
                      style={{
                        transform: language === 'ta' ? 'translateX(24px)' : 'translateX(0)',
                      }}
                    >
                      {language === 'ta' ? 'த' : 'En'}
                    </div>
                  </div>
                </label>
                <button
                  onClick={() => setIsOpen(false)}
                  className="text-white p-1"
                  type="button"
                  aria-label="Close menu"
                >
                  <X size={24} />
                </button>
              </div>
            </div>

            {/* Mobile Menu Items */}
            <div className="p-0 pt-20">
              <Link
                to="/"
                className={`block py-3 px-4 border-b border-gray-100 ${location.pathname === '/' ? 'bg-blue-50 text-blue-700 font-semibold' : 'text-gray-700'
                  } hover:bg-gray-50`}
                onClick={handleMenuItemClick}
              >
                {t?.home || 'Home'}
              </Link>

              <Link
                to="/about-us"
                className="block py-3 px-4 text-gray-700 border-b border-gray-100 hover:bg-gray-50"
                onClick={handleMenuItemClick}
              >
                {t?.about || 'About Us'}
              </Link>

              {/* Departments Dropdown */}
              <div className="border-b border-gray-100">
                <div
                  className="flex items-center justify-between py-3 px-4 text-gray-700 hover:bg-gray-50 cursor-pointer"
                  onClick={(e) => handleDropdownClick('departments', e)}
                >
                  <span>{t?.institutions || 'Institutions'}</span>

                  <ChevronDown size={16} className={`transition-transform duration-200 ${departmentsDropdown ? 'rotate-180' : ''}`} />
                </div>
                <div className={`bg-gray-50 overflow-hidden transition-all duration-200 ${departmentsDropdown ? 'max-h-96' : 'max-h-0'}`}>
                  <div className="border-b border-gray-200">
                    <div className="flex items-center justify-between py-2.5 px-8 text-sm text-gray-600 hover:bg-blue-50 cursor-pointer"
                      onClick={() => setConsumerCoopMobile(prev => !prev)}>
                      Consumer Cooperatives
                      <ChevronDown size={14} className={`${consumerCoopMobile ? "rotate-180" : ""} transition-transform`} />
                    </div>

                    <div className={`overflow-hidden transition-all duration-200 ${consumerCoopMobile ? "max-h-60" : "max-h-0"}`}>
                      <a href="/pds" className="block py-2 px-12 text-sm text-gray-600 hover:bg-blue-50">PDS</a>
                      <a href="/dccws" className="block py-2 px-12 text-sm text-gray-600 hover:bg-blue-50">DCCWS</a>
                      <a href="/dccws" className="block py-2 px-12 text-sm text-gray-600 hover:bg-blue-50">Primary Stores</a>
                      {/* Marundhagam dropdown inside Consumer Cooperatives */}
                      <div
                        className="flex items-center justify-between py-2 px-12 text-sm text-gray-600 hover:bg-blue-50 cursor-pointer"
                        onClick={() => setMarundhagamMobile(prev => !prev)}
                      >
                        <span>Marundhagam</span>
                        <ChevronDown
                          size={12}
                          className={`transition-transform ${marundhagamMobile ? "rotate-180" : ""}`}
                        />
                      </div>

                      <div className={`overflow-hidden transition-all duration-200 ${marundhagamMobile ? "max-h-60" : "max-h-0"}`}>
                        <a
                          href="/institutions/consumer-cooperatives/marundhagam/mudhalvar"
                          className="block py-2 px-16 text-sm text-gray-600 hover:bg-blue-50"
                        >
                          Mudhalvar Marundhagam
                        </a>

                        <a
                          href="/kooturavu-marundhagam"
                          className="block py-2 px-16 text-sm text-gray-600 hover:bg-blue-50"
                        >
                          Kooturavu Marunthagam
                        </a>

                        <a
                          href="/amma-marundhagam"
                          className="block py-2 px-16 text-sm text-gray-600 hover:bg-blue-50"
                        >
                          Amma Marunthagam
                        </a>
                      </div>

                    </div>
                  </div>

                  {/* Banking */}
                  <div
                    className="flex items-center justify-between py-2.5 px-8 text-sm text-gray-700 hover:bg-blue-50 cursor-pointer border-b border-gray-200"
                    onClick={() => setBankingMobile(!bankingMobile)}
                  >
                    <span>Banking</span>
                    <ChevronDown
                      size={14}
                      className={`transition-transform ${bankingMobile ? 'rotate-180' : ''}`}
                    />
                  </div>

                  <div className={`overflow-hidden transition-all duration-200 ${bankingMobile ? 'max-h-60' : 'max-h-0'}`}>
                    <a href="/tnsc" className="block py-2 px-12 text-sm text-gray-600 hover:bg-blue-50">TNSC</a>
                    <a href="/dccwss" className="block py-2 px-12 text-sm text-gray-600 hover:bg-blue-50">DCCB</a>
                    <a href="/ucb" className="block py-2 px-12 text-sm text-gray-600 hover:bg-blue-50">UCB</a>
                  </div>
                  {/* Credit Societies */}
                  <div
                    className="flex items-center justify-between py-2.5 px-8 text-sm text-gray-700 hover:bg-blue-50 cursor-pointer border-b border-gray-200"
                    onClick={() => setCreditMobile(!creditMobile)}
                  >
                    <span>Credit Societies</span>
                    <ChevronDown
                      size={14}
                      className={`transition-transform ${creditMobile ? 'rotate-180' : ''}`}
                    />
                  </div>

                  <div className={`overflow-hidden transition-all duration-200 ${creditMobile ? 'max-h-40' : 'max-h-0'}`}>
                    <a href="/paccs" className="block py-2 px-12 text-sm text-gray-600 hover:bg-blue-50">PACCS</a>
                    <a href="/paccs" className="block py-2 px-12 text-sm text-gray-600 hover:bg-blue-50">UCCS</a>
                  </div>
  {/* Credit Societies */}
                  <div
                    className="flex items-center justify-between py-2.5 px-8 text-sm text-gray-700 hover:bg-blue-50 cursor-pointer border-b border-gray-200"
                    onClick={() => setCreditMobile(!creditMobile)}
                  >
                       <a href="agri-marketing" className="block py-2.5 px-8 text-sm text-gray-600 hover:bg-blue-50 border-b border-gray-200">
                    Agri Marketing Socities
                  </a>
                  </div>
                </div>

              </div>

              {/* Information Dropdown */}
              <div className="border-b border-gray-100">
                <div
                  className="flex items-center justify-between py-3 px-4 text-gray-700 hover:bg-gray-50 cursor-pointer"
                  onClick={(e) => handleDropdownClick('information', e)}
                >
                  <span>{t?.policy || 'Policy'}</span>
                  <ChevronDown size={16} className={`transition-transform duration-200 ${informationDropdown ? 'rotate-180' : ''}`} />
                </div>
                <div className={`bg-gray-50 overflow-hidden overflow-y-auto transition-all duration-200 ${informationDropdown ? 'max-h-80' : 'max-h-0'}`}>
                  <a href="#" className="block py-2.5 px-8 text-sm text-gray-600 hover:bg-blue-50 border-b border-gray-200">
                    Acts & Rules
                  </a>
                  <a href="#" className="block py-2.5 px-8 text-sm text-gray-600 hover:bg-blue-50 border-b border-gray-200">
                    GOs
                  </a>
                  <a href="#" className="block py-2.5 px-8 text-sm text-gray-600 hover:bg-blue-50">
                    Circulars
                  </a>
                </div>
              </div>

              {/* Gallery Dropdown */}
              <div className="border-b border-gray-100">
                <div
                  className="flex items-center justify-between py-3 px-4 text-gray-700 hover:bg-gray-50 cursor-pointer"
                  onClick={(e) => handleDropdownClick('gallery', e)}
                >
                  <span>{t?.services || 'Services'}</span>
                  <ChevronDown size={16} className={`transition-transform duration-200 ${galleryDropdown ? 'rotate-180' : ''}`} />
                </div>
                <div className={`bg-gray-50 overflow-hidden transition-all duration-200 ${galleryDropdown ? 'max-h-40' : 'max-h-0'}`}>
                  <a href="#" className="block py-2.5 px-8 text-sm text-gray-600 hover:bg-blue-50 border-b border-gray-200">
                    eVadagai
                  </a>
                  {/* Banking */}
                  <div
                    className="flex items-center justify-between py-2.5 px-8 text-sm text-gray-700 hover:bg-blue-50 cursor-pointer border-b border-gray-200"
                    onClick={() => setServicesBankMobile(!servicesBankMobile)}
                  >
                    <span>Banking</span>
                    <ChevronDown
                      size={14}
                      className={`transition-transform ${servicesBankMobile ? 'rotate-180' : ''}`}
                    />
                  </div>
                   <a href="/quick-commerce" className="block py-2.5 px-8 text-sm text-gray-600 hover:bg-blue-50 border-b border-gray-200">
                  Quick Commerce
                  </a>
                   <a href="society-registartion" className="block py-2.5 px-8 text-sm text-gray-600 hover:bg-blue-50 border-b border-gray-200">
                   Society Registration
                  </a>
                   <a href="/agri-mechanism" className="block py-2.5 px-8 text-sm text-gray-600 hover:bg-blue-50 border-b border-gray-200">
                  Agri Mechanisation
                  </a>

                  <div className={`overflow-hidden transition-all duration-200 ${servicesBankMobile ? 'max-h-60' : 'max-h-0'}`}>
                    <a href="https://www.rcs.tn.gov.in/loan/" className="block py-2 px-12 text-sm text-gray-600 hover:bg-blue-50">Bank Loan</a>
                    <a href="/services/banking/account-opening" className="block py-2 px-12 text-sm text-gray-600 hover:bg-blue-50">Account Opening</a>
                  </div>

                </div>

              </div>

              {/* Citizen Corner Dropdown */}
              <div className="border-b border-gray-100">
                <div
                  className="flex items-center justify-between py-3 px-4 text-gray-700 hover:bg-gray-50 cursor-pointer"
                  onClick={(e) => handleDropdownClick('citizenCorner', e)}
                >
                  <span>{t?.schemes || 'Schemes'}</span>
                  <ChevronDown size={16} className={`transition-transform duration-200 ${citizenCornerDropdown ? 'rotate-180' : ''}`} />
                </div>
                <div className={`bg-gray-50 overflow-hidden transition-all duration-200 ${citizenCornerDropdown ? 'max-h-60' : 'max-h-0'}`}>
                  <a href="/kcc" className="block py-2.5 px-8 text-sm text-gray-600 hover:bg-blue-50 border-b border-gray-200">
                    KCC
                  </a>
                  <a href="/loan" className="block py-2.5 px-8 text-sm text-gray-600 hover:bg-blue-50 border-b border-gray-200">
                    Loan Mechanisation
                  </a>
                  <a href="/auto" className="block py-2.5 px-8 text-sm text-gray-600 hover:bg-blue-50 border-b border-gray-200">
                    Auto Provision
                  </a>
                  <a href="#" className="block py-2.5 px-8 text-sm text-gray-600 hover:bg-blue-50">
                    Women Land
                  </a>
                      <a href="/thayumanavar" className="block py-2.5 px-8 text-sm text-gray-600 hover:bg-blue-50">
               Thaayumaanavar 
                  </a>
                </div>

              </div>

              {/* Font Size Controls - Mobile Simplified */}
              <div className="border-b border-gray-100 p-4">
                <div className="text-gray-700 font-medium mb-3 text-sm">{t?.fontSize || 'Font Size'}</div>
                <div className="flex items-center gap-3">
                  <button
                    onClick={decreaseFontSize}
                    className="flex-1 py-2.5 px-4 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors text-center font-bold"
                    aria-label={t?.decrease || 'Decrease Font Size'}
                  >
                    A-
                  </button>
                  <button
                    onClick={increaseFontSize}
                    className="flex-1 py-2.5 px-4 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors text-center font-bold"
                    aria-label={t?.increase || 'Increase Font Size'}
                  >
                    A+
                  </button>
                  <button
                    onClick={resetFontSize}
                    className="flex-1 py-2.5 px-4 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors text-center font-semibold text-sm"
                    aria-label={t?.reset || 'Reset Font Size'}
                  >
                    Reset
                  </button>
                </div>
              </div>

              {/* Screen Reader - Mobile */}
              <Link
                to="/screen-reader"
                className="flex items-center gap-3 py-3 px-4 text-gray-700 border-b border-gray-100 hover:bg-gray-50"
                onClick={handleMenuItemClick}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M11 5L6 9H2v6h4l5 4V5z"></path>
                  <path d="M15.54 8.46a5 5 0 0 1 0 7.07"></path>
                  <path d="M19.07 4.93a10 10 0 0 1 0 14.14"></path>
                </svg>
                <span>{t?.screenReader || 'Screen Reader Access'}</span>
              </Link>

              {/* Login Dropdown */}
              <div className="border-b border-gray-100">
                <div
                  className="flex items-center justify-between py-3 px-4 text-gray-700 hover:bg-gray-50 cursor-pointer"
                  onClick={() => setLoginDropdown(!loginDropdown)}
                >
                  <span>{t?.login || 'Login'}</span>
                  <ChevronDown size={16} className={`transition-transform duration-200 ${loginDropdown ? 'rotate-180' : ''}`} />
                </div>
                <div className={`bg-gray-50 overflow-hidden transition-all duration-200 ${loginDropdown ? 'max-h-48' : 'max-h-0'}`}>
                  <a href="http://ercs.kooturavu.tn.gov.in/" target="_blank" rel="noopener noreferrer" className="block py-2.5 px-8 text-sm text-gray-600 hover:bg-blue-50 border-b border-gray-200" onClick={handleMenuItemClick}>
                    eRCS
                  </a>
                  <a href="https://www.santhai.kooturavu.tn.gov.in/" target="_blank" rel="noopener noreferrer" className="block py-2.5 px-8 text-sm text-gray-600 hover:bg-blue-50 border-b border-gray-200" onClick={handleMenuItemClick}>
                    DMS
                  </a>
                  <a href="https://dashboard.kooturavu.tn.gov.in/" target="_blank" rel="noopener noreferrer" className="block py-2.5 px-8 text-sm text-gray-600 hover:bg-blue-50 border-b border-gray-200" onClick={handleMenuItemClick}>
                    Dashboard
                  </a>
                  <a href="#" className="block py-2.5 px-8 text-sm text-gray-600 hover:bg-blue-50" onClick={handleMenuItemClick}>
                    LIST
                  </a>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </header>
  );
};

export default Navbar;